import scrollTo from 'gatsby-plugin-smoothscroll';
import React, { useEffect, useRef, useState } from 'react';
import { classnames } from 'tailwindcss-classnames';

import Search from '../../assets/svg/search.svg';
import { BREAKPOINTS } from '../../constants/breakpoints';
import { useHeader } from '../../graphql/hooks/use-header';
import Link from '../Link';
import Logo from '../Logo';
import { ExpandedNav, HamburgerMenu } from '../Navigation';
import HeaderTopics from './HeaderTopics';
import {
  DEFAULT_THEME,
  getCurrentTime,
  getHeaderTheme,
  handleHeaderColorBreakpoint,
  minifiedHeaderClasses,
  nonMinifiedHeaderClasses,
} from './HeaderUtils';

/*
  Header summary
  The header needs to display differently across many different types of templates
  The main state changes are color changes, minification, and the expanded view
*/

const Header = ({
  backgroundColor = DEFAULT_THEME.backgroundColor,
  breakpointRef,
  children,
  isHomepage = false,
  showBorder,
  showFreddieInverse,
  showOverlayMode,
  showShortLogo,
  showTime,
  showTopics,
  textColor = DEFAULT_THEME.textColor,
}) => {
  const data = useHeader();
  const { featuredTopics, mainNav } = data;
  const offsetYRef = useRef(0);
  const headerRef = useRef(null);
  const [isExpanded, setExpanded] = useState(false);
  const [isMinified, setMinified] = useState(false);
  const [isScrollingDown, setScrollingDown] = useState(false);
  const [isHeaderOverColorBreakpoint, setHeaderOverColorBreakpoint] =
    useState(false);
  const fFeaturedTopics = featuredTopics.filter(x=> x.slug !== 'empty')

  const isMinifiedState = showOverlayMode || isMinified;

  // handles the minification and color breakpoint function
  const handleScroll = () => {
    const currentOffsetY = window.pageYOffset;
    if (currentOffsetY > 0) {
      setMinified(true);
      setScrollingDown(currentOffsetY > offsetYRef.current);
    } else {
      setMinified(false);
    }

    setHeaderOverColorBreakpoint(
      handleHeaderColorBreakpoint(breakpointRef, headerRef),
    );

    offsetYRef.current = currentOffsetY;
  };

  // handles either the opening or closing of the expanded navigation
  const toggleExpandedNav = () => {
    setExpanded((currVal) => !currVal);
  };

  // todo: refactor the expanded nav
  useEffect(() => {
    const windowSize = window.innerWidth;
    if (windowSize < BREAKPOINTS.desktopXS) {
      document.body.style.overflowY = `${isExpanded ? 'hidden' : 'visible'}`;
    }
  });

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrollingDown, isMinified]);

  const theme = getHeaderTheme({
    isExpanded,
    isHeaderOverColorBreakpoint,
    showFreddieInverse,
    showOverlayMode,
    theme: { backgroundColor, textColor },
  });

  return (
    <>
      <div
        ref={headerRef}
        className={classnames(
          'sm:relative',
          isMinifiedState ? 'fixed' : '',
          'top-0',
          'z-40',
          'sm:top-auto',
        )}
        data-module-id="header_nav"
        role="banner"
        style={{
          '--header-background-color': theme.backgroundColor,
          '--header-text-color': theme.textColor,
        }}
      >
        {/* shrink nav part - always fixed on tablet and desktop, - making this sticky doesn't work as the animation causes it to jerk back */}
        <div
          className={classnames(
            isMinifiedState ? 'fixed' : '',
            'sm:block',
            'top-0',
            'left-0',
            'right-0',
            'z-40',
            'bg-[color:var(--header-background-color)]',
            'transition-all',
            'ease-in-out',
            'duration-300',
            { 'hover:bg-liquorice': showOverlayMode },
          )}
        >
          {/* nav container */}
          <div
            className={classnames(
              'ml-5',
              'mr-5',
              'sm:ml-12',
              'sm:mr-12',
              'flex',
              'items-center',
              'transition-all',
              'ease-in-out',
              'duration-300',
              'justify-between',
              minifiedHeaderClasses,
            )}
          >
            {/*  */}
            <div
              className="flex items-center flex-1 h-full"
              data-component-id="header_logo"
            >
              <Logo
                backgroundColor={theme.freddieBackgroundColor}
                className="h-full"
                foregroundColor={theme.freddieForegroundColor}
                inverse={theme.isFreddieInverse}
                showShortLogo={showShortLogo}
              />
              {showTime && (
                <h5 className="ml-3 stink-h5">{getCurrentTime()}</h5>
              )}
            </div>
            <div className="flex items-center">
              {/* todo: fix underline animate so padding is not needed */}
              <div className="hidden lg:inline">
                {mainNav?.map(({ id, title, url }, index) => (
                  String(url).includes('https://') ? 
                  <a
                  key={id}
                  className={classnames(
                    'pb-2',
                    'rga-link',
                    'animate',
                    'transition-color',
                    'ease-in-out',
                    'duration-300',
                    {
                      'ml-6': index > 0,
                    },
                    'text-[color:var(--header-text-color)]',
                  )}
                  data-component-id="header_logo_link"
                  style={{ '--anchor-animation-color': theme.textColor }}
                  href={url}
                >
                  {title}
                </a>
                
                  :<Link
                    key={id}
                    className={classnames(
                      'pb-2',
                      'rga-link',
                      'animate',
                      'transition-color',
                      'ease-in-out',
                      'duration-300',
                      {
                        'ml-6': index > 0,
                      },
                      'text-[color:var(--header-text-color)]',
                    )}
                    data-component-id="header_logo_link"
                    style={{ '--anchor-animation-color': theme.textColor }}
                    to={url}
                  >
                    {title}
                  </Link>
                ))}
              </div>
              {isHomepage && (
                <div className="ml-0 sm:ml-6">
                  <button
                    className="nav__search"
                    data-component-id="nav_search"
                    onClick={() => scrollTo('#search-section', 'center')}
                    type="button"
                  >
                    <Search className="w-4 sm:w-5 mx-3 max-h-14 pt-2" />
                  </button>
                </div>
              )}
              <div className="ml-0 sm:ml-6">
                <HamburgerMenu
                  color={theme.hamburgerColor}
                  data-component-id="hamburger_menu"
                  minified={isMinifiedState}
                  onClick={() => {
                    toggleExpandedNav();
                  }}
                  open={isExpanded}
                  scrollDown={isScrollingDown}
                />
              </div>

              <ExpandedNav
                data={data}
                onClick={() => {
                  toggleExpandedNav();
                }}
                open={isExpanded}
              />
            </div>
          </div>

          {/* border */}
          {showBorder && (
            <div
              className={classnames(
                'h-px',
                'bg-[color:var(--header-text-color)]',
                { 'opacity-[.15]': isMinifiedState },
                'ease-in-out',
                'duration-300',
                'transition-all',
                isMinifiedState ? 'mx-0' : 'mx-5',
              )}
            />
          )}
        </div>
      </div>
      {showTopics && <HeaderTopics topics={fFeaturedTopics} />}
    </>
  );
};

export default Header;
